import React from "react";
import {
	Control,
	Controller,
	ControllerFieldState,
	ControllerProps,
	ControllerRenderProps,
	FieldValues,
	UseControllerProps,
	UseFormStateReturn,
} from "react-hook-form";
import { renderErrorMessages } from "@/main/services/utils";
import { currencyInputMask, formatCurrency, getSeparatorDecimalByCulture } from "@/main/services/utilsNumber";
import KogInputTextOutlined from "./inputTextOutlined";
import KogInputText from "./inputText";

type IControllerProps = Omit<ControllerProps, "render" | "name">;
type IHTMLInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "number" | "color" | "size">;
interface IKogInputAmountControlledProps {
	id: string;
	typeInput?: "outlined" | "filled" | "outlinedfull";
	required?: boolean;
	errorMessageCustom?: string;
	controllerProps?: IControllerProps;
	label?: string;
	customInputProps?: IHTMLInputProps;
	disabledMessage?: boolean;
	disabled?: boolean;
	hideValue?: boolean;
	acceptZero?: boolean;
	onChangeCustom?: (value: string) => void;
	onBlurCustom?: (value: string) => void;
	onClickCustom?: (value: React.MouseEvent<HTMLDivElement>) => void;
	onKeyDown?: (e: React.KeyboardEvent) => void;
}

/**
 * Component to render a controlled input with currency mask.
 *
 * @param {string} id - Id of the input
 * @param {string} name - Name of the input
 * @param {Control<FieldValues>} control - Control of the form
 * @param {boolean} required - If true, the input will be required
 * @param {string} errorMessageCustom - Custom error message
 * @param {string} label - Label of the input
 * @param {IControllerProps} controllerProps - Props of the controller
 * @param {string} typeInput - Type of the input
 * @param {React.InputHTMLAttributes<HTMLInputElement>} customInputProps - Custom props of the input
 * @param {boolean} disabledMessage - If true, the error message will be disabled
 * @param {boolean} disabled - If true, the disabled
 * @param {boolean} hideValue - If true, the value will be hidden
 * @param {boolean} acceptZero - If true, the input will accept zero value
 * @param {Function} onClickCustom - Function to be executed when the input is clicked
 * @param {Function} onChangeCustom - Function to be executed when the value changes
 * @param {Function} onBlurCustom - Function to be executed when the value changes
 * @example
 * const { control } = useForm(); //react-hoook-form
 *
 *  <KogInputAmountControlled
 *    id='inputtest'
 *    name="inputtest"
 *    control={control}
 *  />
 */
function KogInputAmountControlled<TFieldValues extends FieldValues>({
	id,
	name,
	control,
	required = false,
	errorMessageCustom,
	typeInput = "outlined",
	label = "",
	controllerProps = {},
	customInputProps = {},
	disabledMessage = false,
	disabled = false,
	onChangeCustom = () => {},
	onBlurCustom = () => {},
	onClickCustom = () => {},
	hideValue = false,
	acceptZero = false,
	onKeyDown,
}: Required<Pick<UseControllerProps<TFieldValues>, "name" | "control">> & IKogInputAmountControlledProps) {
	const renderInput = ({
		field,
		fieldState: { error },
	}: {
		field: ControllerRenderProps<FieldValues, string>;
		fieldState: ControllerFieldState;
		formState: UseFormStateReturn<FieldValues>;
	}) => {
		/**
		 * Notation:
		 * If the value is a number, it's because a value that came from the database,
		 * so it needs to be formatted as currency.
		 */
		const validateValue = typeof field.value === "number" ? formatCurrency(field.value, acceptZero) : field.value;

		function getInputValue(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
			const eventValue = e.target.value;
			const oldValue = field.value;

			if (
				acceptZero &&
				(eventValue === "" || (eventValue.replace(/\D/g, "") === "00" && oldValue.replace(/\D/g, "") === "000"))
			) {
				return "";
			}

			const validateValue = currencyInputMask(e.target.value, oldValue, acceptZero);

			// Notation: This is to keep the cursor in the same position
			const hasLastIndexChar = e.target.selectionStart === e.target.value.length;
			const positionChanged = hasLastIndexChar ? validateValue.length : e.target.selectionStart;
			e.target.value = validateValue;
			e.target.setSelectionRange(positionChanged, positionChanged);
			return validateValue;
		}

		const onChangeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const validateValue = getInputValue(e);

			field.onChange(validateValue);
			onChangeCustom(validateValue);
		};

		const onBlurInput = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			field.onBlur();

			const value = e.target.value;

			if (!value) {
				onBlurCustom(value);
				return;
			}

			const { separatorDecimal } = getSeparatorDecimalByCulture();

			let finalValue: string;
			if (value.includes(separatorDecimal)) {
				const [intPart, decPart] = value.split(separatorDecimal);
				finalValue = `${intPart}${separatorDecimal}${(decPart || "").padEnd(2, "0")}`;
			} else {
				finalValue = `${value}${separatorDecimal}00`;
			}

			if (finalValue !== value) {
				field.onChange(finalValue);
			}

			onBlurCustom(finalValue);
		};

		const baseInputProps: IHTMLInputProps = {
			...field,
			...customInputProps,
			id: id,
			required: required,
			value: hideValue ? "" : validateValue,
			onChange: onChangeInput,
			onBlur: onBlurInput,
		};

		switch (typeInput) {
			case "outlined":
				return (
					<KogInputTextOutlined
						{...baseInputProps}
						label={label}
						error={!!error}
						onClick={onClickCustom}
						onKeyDown={onKeyDown}
						disabled={disabled}
					/>
				);
			case "filled":
				return (
					<KogInputText
						{...baseInputProps}
						variant="filled"
						label={label}
						error={!!error}
						helperText={!disabledMessage && (errorMessageCustom || renderErrorMessages(label, error))}
						onClick={onClickCustom}
						onKeyDown={onKeyDown}
						disabled={disabled}
					/>
				);
			case "outlinedfull":
				return (
					<KogInputText
						{...baseInputProps}
						variant="outlined"
						label={label}
						error={!!error}
						helperText={!disabledMessage && (errorMessageCustom || renderErrorMessages(label, error))}
						onClick={onClickCustom}
						onKeyDown={onKeyDown}
						disabled={disabled}
					/>
				);
			default:
				return <input {...baseInputProps} />;
		}
	};
	const customControllerProps: IControllerProps = {
		...controllerProps,
		...{ rules: { required } },
	};

	return (
		<Controller
			name={name}
			control={control as Control<FieldValues>}
			defaultValue={""}
			{...customControllerProps}
			render={renderInput}
		/>
	);
}

export default KogInputAmountControlled;
